import { computed } from 'vue'
import { createPriceBreakdown } from '../lib/calculationUtils'
import { findUnavailableItems } from '../lib/unavailableItems'
import { useDeliveryCalculation } from './calculations/useDeliveryCalculation'
import { useLoyaltyCalculation } from './calculations/useLoyaltyCalculation'
import { usePriceCalculation } from './calculations/usePriceCalculation'
import { useOrderPreview } from './calculations/useOrderPreview'
import { useBasketState } from './useBasketState'
import { useCheckoutFormStore } from './useCheckoutFormStore'
import { useAnalytics } from '@shared/lib'
import { useInstitutionStore , useWorkingHours, useBranchAvailability } from '@entities/institution'

/**
 * ✅ ГЛАВНЫЙ COMPOSABLE КОРЗИНЫ - единая точка входа
 * 
 * Объединяет все маленькие composables и предоставляет ЕДИНЫЙ API для компонентов
 * 
 * Компоненты должны использовать ТОЛЬКО этот composable, а не лезть напрямую в stores!
 * 
 * Преимущества:
 * - Инкапсуляция: компоненты не знают про внутреннюю структуру
 * - Простота: один импорт вместо 8 stores
 * - Тестируемость: легко мокать через DI
 */
export function useBasket() {
  const basketState = useBasketState()
  const institution = useInstitutionStore()
  const { track } = useAnalytics()
  const workingHours = useWorkingHours()
  const availability = useBranchAvailability()
  
  // Подключаем маленькие composables для расчетов
  const delivery = useDeliveryCalculation()
  const loyalty = useLoyaltyCalculation()
  const price = usePriceCalculation()
  // PREVIEW-расчёт на бэкенде — единый источник истины по деньгам (сбор/итог/баллы/
  // приветственная скидка). Локальные расчёты выше остаются оптимистичным фолбэком.
  const preview = useOrderPreview()
  
  // ========================================
  // СОСТОЯНИЕ (из basketState)
  // ========================================
  
  /**
   * Товары в корзине (обернуты в computed для сохранения реактивности)
   * ⚠️ Важно: Pinia unwrap'ит computed refs, поэтому нужна повторная обертка
   */
  const cartItems = computed(() => basketState.cartItems)
  
  /**
   * Общая стоимость товаров (обернута в computed для сохранения реактивности)
   */
  const cartTotal = computed(() => basketState.cartTotal)
  
  /**
   * Количество товаров (обернуто в computed для сохранения реактивности)
   */
  const cartItemsCount = computed(() => basketState.cartItemsCount)
  
  // ========================================
  // ПРОМОКОД
  // ========================================
  
  /**
   * Код промокода
   */
  const promocode = computed({
    get: () => basketState.promocode,
    set: (value) => { basketState.promocode = value },
  })
  
  /**
   * Применен ли промокод (напрямую, уже computed)
   */
  const isPromocodeApplied = basketState.isPromocodeApplied
  
  /**
   * Скидка от промокода (напрямую, уже computed)
   */
  const promocodeDiscount = basketState.promocodeDiscount
  
  /**
   * Бесплатная доставка от промокода (напрямую, уже computed)
   */
  const isFreeDelivery = basketState.isFreeDelivery
  
  /**
   * Совместим ли промокод с баллами (напрямую, уже computed)
   */
  const isPromocodeCompatibleWithLoyalty = basketState.isPromocodeCompatibleWithLoyalty
  
  /**
   * Ошибка промокода (напрямую, уже computed)
   */
  const promocodeError = basketState.promocodeError
  
  /**
   * Загрузка промокода (напрямую, уже computed)
   */
  const promocodeLoading = basketState.promocodeLoading
  
  // ========================================
  // БАЛЛЫ ЛОЯЛЬНОСТИ
  // ========================================
  
  /**
   * Использовать ли баллы
   */
  const useLoyaltyPoints = computed({
    get: () => basketState.useLoyaltyPoints,
    set: (value) => basketState.toggleLoyaltyPoints(value),
  })
  
  /**
   * Количество баллов к списанию
   */
  const pointsToUse = computed({
    get: () => basketState.pointsToUse,
    set: (value) => basketState.setPointsToUse(value),
  })
  
  /**
   * Доступные баллы
   * ⚠️ ВАЖНО: Оборачиваем в computed для сохранения реактивности
   */
  const availablePoints = computed(() => basketState.availablePoints)
  
  /**
   * Максимум баллов к списанию
   * ⚠️ ВАЖНО: Оборачиваем в computed для сохранения реактивности
   */
  const maxPointsToUse = computed(() => basketState.maxPointsToUse)
  
  /**
   * Кешбэк за заказ
   * ⚠️ ВАЖНО: Оборачиваем в computed для сохранения реактивности
   */
  const cashbackAmount = computed(() => basketState.cashbackAmount)
  
  /**
   * Активный сценарий preview (баллы / приветственная скидка) — null пока не загружен.
   */
  const previewScenario = computed(() => preview.activeScenario)

  /**
   * Форматированная информация о лояльности.
   * Кешбэк (cashbackAmount) берём из preview-расчёта (точные баллы к начислению с учётом
   * приветственной скидки), с фолбэком на локальный расчёт пока ответ не пришёл.
   */
  const formattedLoyaltyInfo = computed(() => {
    const local = loyalty.formattedInfo.value
    const scenario = previewScenario.value
    if (!scenario) return local
    return { ...local, cashbackAmount: scenario.pointsToEarn }
  })

  /**
   * Информация о приветственной скидке для UI выбора «баллы ИЛИ скидка».
   * available — доступна ли скидка пользователю (есть, если не списываются баллы);
   * discount — её размер в активном сценарии (0 в режиме баллов).
   */
  const onboardingInfo = computed(() => ({
    available: Boolean(preview.preview?.onboarding.available),
    discount: preview.preview?.onboarding.discount ?? 0,
    appliedDiscount: previewScenario.value?.onboardingDiscount ?? 0,
    // Скидка существует, но погашена применённым промокодом — для подсказки в корзине.
    suppressedByPromocode: Boolean(preview.preview?.onboarding.suppressedByPromocode),
  }))
  
  /**
   * Какую долю заказа заведение разрешает оплатить баллами (0.5 = 50%).
   * Настраивается в админке и у разных заведений разная — подпись «Максимум»
   * в корзине обязана показывать реальный лимит, а не константу.
   */
  const maxRedeemPercent = computed(
    () => institution.institution?.loyalty_max_percent_redeem ?? 0.5,
  )

  /**
   * Ограничение на СПИСАНИЕ баллов из настроек заведения (мин. сумма заказа и
   * «списание с N-го заказа»). Источник истины — preview-расчёт: только бэкенд знает,
   * сколько заказов гость уже оплатил.
   *
   * Возвращаем null, когда списание разрешено или причина не касается настроек
   * (нет баллов / лояльность выключена — это UI показывает и без нас).
   */
  const pointsRedeemRestriction = computed(() => {
    const redeem = preview.preview?.pointsRedeem
    if (redeem) {
      if (redeem.available) return null
      if (redeem.reason !== 'min_order_amount' && redeem.reason !== 'min_paid_orders') return null

      return {
        reason: redeem.reason,
        minOrderAmount: redeem.minOrderAmount,
        fromOrderNumber: redeem.fromOrderNumber,
      }
    }

    // Preview ещё не пришёл или не считается вовсе (доставка без указанного адреса —
    // useOrderPreview в этом случае не шлёт запрос). Порог суммы знаем из настроек
    // заведения, поэтому проверяем его сами: иначе гость вводит баллы в форму,
    // которую бэкенд всё равно отклонит на оформлении.
    // Гостю с нулевым балансом порог не сообщаем: «добери до 500 ₽, чтобы списать
    // баллы» — совет списать то, чего нет. Блок просто покажет «Доступно: 0 баллов».
    if (basketState.availablePoints <= 0) return null

    // Сравниваем с суммой товаров ДО промокода — так же, как считает бэкенд.
    const minOrderAmount = Number(institution.institution?.loyalty_redeem_min_order_amount ?? 0)
    if (minOrderAmount > 0 && basketState.cartTotal < minOrderAmount) {
      return {
        reason: 'min_order_amount' as const,
        minOrderAmount,
        fromOrderNumber: Number(institution.institution?.loyalty_redeem_from_order_number ?? 1),
      }
    }

    // Ограничение «с N-го заказа» локально проверить нечем: сколько заказов гость
    // оплатил, знает только сервер. Оставляем его на preview и бэкенд.
    return null
  })

  /**
   * Включена ли программа лояльности в заведении.
   * Гейтим СТРОГО по мастер-флагу loyalty_enabled (= loyalty_settings.enabled,
   * тоггл в админке). Раньше был фолбэк `|| loyalty_levels.length > 0` — из-за него
   * при выключенной лояльности секция баллов в корзине НЕ скрывалась (уровни остаются
   * в БД), хотя бэкенд начисление уже не делает и профиль /lk прячет блок. Баг рассинхрона.
   */
  const isLoyaltyEnabled = computed(() => Boolean(institution.institution?.loyalty_enabled))
  
  // ========================================
  // ДОСТАВКА
  // ========================================
  
  /**
   * Это заказ на доставку? (напрямую, уже computed)
   */
  const isDeliveryOrder = delivery.isDeliveryOrder
  
  /**
   * Стоимость доставки (напрямую, уже computed)
   */
  const deliveryCost = basketState.deliveryCost
  
  /**
   * Минимальная сумма заказа
   * ✅ Используем напрямую из delivery для актуальности
   */
  const minOrderAmount = delivery.minOrderAmount
  
  /**
   * Правила доставки
   */
  const deliveryRules = delivery.deliveryRules
  
  /**
   * Текущая зона доставки (напрямую, уже computed)
   */
  const currentDeliveryZone = delivery.currentZone
  
  /**
   * Информация о доставке (форматированная)
   */
  const deliveryInfo = computed(() => {
    const zone = delivery.currentZone.value
    if (!zone) {
      return {
        cost: 0,
        minAmount: 0,
        toMinOrder: 0,
        toFreeDelivery: null,
        isFreeAvailable: false,
      }
    }
    
    return {
      cost: deliveryCost,
      minAmount: minOrderAmount,
      toMinOrder: Math.max(minOrderAmount - cartTotal.value, 0),
      toFreeDelivery: null, // TODO: implement if needed
      isFreeAvailable: false, // TODO: implement if needed
    }
  })
  
  // ========================================
  // РАСЧЕТЫ
  // ========================================
  
  /**
   * Процент сервисного сбора
   */
  const serviceFeePercent = computed(() => price.serviceFeePercent)
  
  /**
   * Сумма сервисного сбора
   */
  const serviceFee = computed(() => basketState.serviceFee)
  
  /**
   * Финальная сумма к оплате.
   * Из preview-расчёта (итог активного сценария), фолбэк — локальный basketState.finalAmount.
   */
  const finalAmount = computed(() => previewScenario.value?.totalAmount ?? basketState.finalAmount)

  /**
   * Полный расчет цен (напрямую, уже computed)
   */
  const priceCalculation = price.priceCalculation

  /**
   * Детализация цен для UI.
   * При наличии preview строим разбивку из ответа бэкенда (включая приветственную скидку),
   * иначе — локальный плейсхолдер price.priceBreakdown.
   */
  const priceBreakdown = computed(() => {
    const scenario = previewScenario.value
    const p = preview.preview
    if (!scenario || !p) {
      return price.priceBreakdown.value
    }
    return createPriceBreakdown({
      cartTotal: basketState.cartTotal,
      promocodeDiscount: scenario.promocodeDiscount,
      pointsDiscount: scenario.pointsUsed,
      onboardingDiscount: scenario.onboardingDiscount,
      // Без этой строки детализация не сходится: cartTotal включает подарок по полной
      // цене, а totalAmount с бэка уже за вычетом его стоимости.
      giftTierDiscount: scenario.giftTierDiscount ?? 0,
      deliveryCost: p.deliveryCost,
      serviceFee: scenario.serviceFee,
      finalAmount: scenario.totalAmount,
    })
  })
  
  // ========================================
  // ВАЛИДАЦИЯ И СОСТОЯНИЕ ЗАКАЗА
  // ========================================
  
  /**
   * Текущий способ получения заказа (delivery / takeaway / dine_in)
   */
  const currentMethodReceipt = computed(
    () => useCheckoutFormStore().OrderForm.method_receipt,
  )

  /**
   * Причина блокировки от админ-панели «Управление загрузкой».
   * null = нет блокировки от workload-панели.
   */
  const workloadBlockReason = computed(() =>
    availability.blockReasonFor(currentMethodReceipt.value),
  )

  /**
   * Позиции, которых в выбранном филиале нет: набор не продаётся здесь, блюдо слота
   * не готовят, товар снят с продажи или остался в корзине после смены филиала.
   * Правило общее с карточкой списка — см. features/basket/lib/unavailableItems.
   */
  const unavailableItems = computed(() => {
    const allProducts: any[] = []
    for (const category of (institution.products as any[]) || []) {
      if (Array.isArray(category?.products)) allProducts.push(...category.products)
    }

    return findUnavailableItems(cartItems.value as any[], allProducts)
  })

  /**
   * Заблокирован ли заказ
   */
  const isOrderDisabled = computed(() => {
    // 0. Приостановка оплаты/заказов в админке («Не выбран» провайдер)
    if (institution.institution?.payment_paused) {
      return true
    }

    // 0. СТОП Приём заказов / СТОП Доставка (с учётом выбранного способа)
    if (workloadBlockReason.value) {
      return true
    }

    // 1. Проверяем рабочее время
    if (!workingHours.isCurrentBranchOpen.value) {
      return true
    }

    // 2. Минимальная сумма для доставки
    if (delivery.isDeliveryOrder.value && cartTotal.value < minOrderAmount) {
      return true
    }

    // 3. Пустая корзина
    if (cartItemsCount.value === 0) {
      return true
    }

    // 4. В корзине есть позиция, которую в этом филиале не заказать. Сервер такой заказ
    // всё равно отклонит — блокируем раньше, чтобы человек не упирался в отказ на оплате.
    if (unavailableItems.value.length > 0) {
      return true
    }

    return false
  })

  /**
   * Текст подсказки для заблокированного заказа
   */
  const tooltipText = computed(() => {
    if (institution.institution?.payment_paused) {
      return 'Оплата временно приостановлена'
    }

    if (workloadBlockReason.value) {
      return workloadBlockReason.value
    }

    if (!workingHours.isCurrentBranchOpen.value) {
      return 'Мы закрыты'
    }

    if (delivery.isDeliveryOrder.value && cartTotal.value < minOrderAmount) {
      return `Минимальная сумма заказа для доставки: ${minOrderAmount} ₽`
    }

    if (cartItemsCount.value === 0) {
      return 'Добавьте товары в корзину'
    }

    if (unavailableItems.value.length > 0) {
      const names = unavailableItems.value
        .map((item: any) => item?.name)
        .filter(Boolean)
        .slice(0, 2)
        .join(', ')

      return names
        ? `Уберите из корзины: ${names}`
        : 'В корзине есть позиции, недоступные в этом филиале'
    }

    return ''
  })

  /**
   * Текст кнопки заказа
   */
  const orderButtonLabel = computed(() => {
    if (availability.isFullyBlocked.value) {
      return 'Заказ временно недоступен'
    }
    if (currentMethodReceipt.value === 'delivery' && availability.deliveryStopped.value) {
      return 'Доставка остановлена'
    }
    if (!workingHours.isCurrentBranchOpen.value) {
      return 'Мы закрыты'
    }

    return 'Оформить заказ'
  })
  
  // ========================================
  // ACTIONS - БАЛЛЫ
  // ========================================
  
  /**
   * Обработать ввод баллов
   */
  function handlePointsInput(): void {
    loyalty.handlePointsInput(basketState.pointsToUse)
  }
  
  /**
   * Установить максимум баллов
   */
  function setMaxPoints(): void {
    loyalty.setMaxPoints()
  }
  
  /**
   * Валидировать использование баллов
   */
  function validatePointsUsage(): boolean {
    if (!basketState.useLoyaltyPoints) return true
    return loyalty.isPointsValid.value
  }
  
  /**
   * Загрузить информацию о лояльности
   */
  async function loadLoyaltyInfo(): Promise<void> {
    await loyalty.loadLoyaltyInfo()
  }
  
  // ========================================
  // ACTIONS - ПРОМОКОД (TODO: переместить из usePromocode)
  // ========================================
  
  /**
   * Применить промокод
   * TODO: Переместить логику из usePromocode сюда
   */
  function applyPromocode(result: {
    discount: number
    freeDelivery: boolean
    compatibleWithLoyalty: boolean
    promocodeId: string
  }): void {
    basketState.applyPromocode(result)
    
    // Если промокод несовместим с баллами - автоматически отключаем
    if (!result.compatibleWithLoyalty && basketState.useLoyaltyPoints) {
      basketState.toggleLoyaltyPoints(false)
    }
  }
  
  /**
   * Сбросить промокод
   */
  function resetPromocode(): void {
    basketState.resetPromocode()
  }
  
  // ========================================
  // ACTIONS - ФОРМА
  // ========================================
  
  /**
   * Обновить форму заказа с баллами
   * (для совместимости со старым кодом)
   */
  function updateOrderFormWithPoints(): void {
    const checkoutForm = useCheckoutFormStore()
    // Гейтим и по мастер-флагу: если лояльность выключили в админке, баллы не шлём,
    // даже если тоггл остался включён со стейта до выключения (бэкенд их тоже обнулит).
    const pointsAmount =
      isLoyaltyEnabled.value && basketState.useLoyaltyPoints ? basketState.pointsToUse : 0
    checkoutForm.setField('pointsToUse', pointsAmount)

    // Списание баллов — отдельный шаг воронки: гейты «от суммы» и «с N-го заказа»
    // молча обнуляют скидку, и без этого события такой отвал в отчёте не отличить
    // от того, что баллы просто не захотели тратить.
    if (basketState.useLoyaltyPoints) {
      const block = pointsRedeemRestriction.value
      track('checkout_points', {
        amount: pointsAmount,
        ok: pointsAmount > 0 && !block,
        reason: block?.reason,
      })
    }
  }
  
  // ========================================
  // RETURN PUBLIC API
  // ========================================
  
  return {
    // Корзина
    cartItems,
    cartTotal,
    cartItemsCount,
    
    // Промокод
    promocode,
    isPromocodeApplied,
    promocodeDiscount,
    isFreeDelivery,
    isPromocodeCompatibleWithLoyalty,
    promocodeError,
    promocodeLoading,
    applyPromocode,
    resetPromocode,
    
    // Баллы лояльности
    useLoyaltyPoints,
    pointsToUse,
    availablePoints,
    maxPointsToUse,
    cashbackAmount,
    formattedLoyaltyInfo,
    onboardingInfo,
    pointsRedeemRestriction,
    maxRedeemPercent,
    isLoyaltyEnabled,
    handlePointsInput,
    setMaxPoints,
    validatePointsUsage,
    loadLoyaltyInfo,
    
    // Доставка
    isDeliveryOrder,
    deliveryCost,
    minOrderAmount,
    deliveryRules,
    currentDeliveryZone,
    deliveryInfo,
    
    // Расчеты
    serviceFeePercent,
    serviceFee,
    finalAmount,
    priceCalculation,
    priceBreakdown,
    
    // Состояние заказа
    isOrderDisabled,
    tooltipText,
    orderButtonLabel,
    
    // Working hours (проброс)
    isCurrentBranchOpen: workingHours.isCurrentBranchOpen,
    workingStatus: workingHours.workingStatus,
    
    // Actions
    updateOrderFormWithPoints,
  }
}

// Для импорта типа
export type UseBasket = ReturnType<typeof useBasket>

